import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  BarChart3,
  FlaskConical,
  HeartPulse,
  LifeBuoy,
  TrendingDown,
  type LucideIcon,
} from "lucide-react";
import {
  experimentPhase,
  type InsightRecord,
  type KpiDefinition,
  type KpiStatusRow,
  type PlaybookObjectiveRecord,
} from "@graftus/schema";

import { resolveInsightIcon } from "@/components/nudges/icon-map";
import { useKpiDefinitions, useKpiStatus } from "@/hooks/queries/use-kpi";
import { usePlaybookObjectives } from "@/hooks/queries/use-playbook";
import { DASHBOARD_TRPC_STALE_MS } from "@/lib/dashboard-trpc-stale";
import { formatIsoDate } from "@/lib/playbook-dates";
import { useTRPC } from "@/trpc/client";
import type { RailStarter } from "./rail-starter";

/** How many cards the empty rail shows. */
export const RAIL_STARTER_COUNT = 3;

type TranslateFn = (key: string, opts?: Record<string, unknown>) => string;

export interface RailStarterInputs {
  /** Server insights, newest generation, not dismissed. */
  insights: InsightRecord[];
  /** Nightly KPI status rows (any subject; team rows are used). */
  kpiRows: KpiStatusRow[];
  kpiDefinitions: KpiDefinition[];
  /** Playbook objectives; those with an experiment window are considered. */
  objectives: PlaybookObjectiveRecord[];
  now: Date;
  language: string;
  t: TranslateFn;
}

const ACCENT_RANK: Record<string, number> = { danger: 0, warning: 1 };
const KPI_STATUS_RANK: Partial<Record<KpiStatusRow["status"], number>> = {
  off_track: 0,
  stalling: 1,
};

const GENERIC: [string, LucideIcon][] = [
  ["performance", BarChart3],
  ["support", LifeBuoy],
  ["wellbeing", HeartPulse],
];

/**
 * Empty-rail starters drawn from the business's real signals, in priority
 * order: AI/data insights that carry a prompt, then team KPIs the nightly job
 * marked off track or stalling, then change experiments that are ending or
 * awaiting a verdict. Generic evergreen questions only fill remaining slots,
 * so the rail never asserts a fact (an off-track KPI, a running experiment)
 * the data does not back.
 */
export function buildRailStarters(inputs: RailStarterInputs): RailStarter[] {
  const { t, now, language } = inputs;
  const out: RailStarter[] = [];
  const take = (s: RailStarter) => {
    if (out.length < RAIL_STARTER_COUNT) out.push(s);
  };

  // 1. Insights with a prompt: most urgent accent first, then priority.
  const insights = inputs.insights
    .filter((i) => i.suggestedPrompt)
    .sort(
      (a, b) =>
        (ACCENT_RANK[a.accent] ?? 2) - (ACCENT_RANK[b.accent] ?? 2) ||
        b.priority - a.priority,
    );
  for (const i of insights) {
    const reasonKey =
      i.accent === "danger"
        ? "danger"
        : i.accent === "warning"
          ? "warning"
          : "default";
    take({
      id: `insight:${i.id}`,
      icon: resolveInsightIcon(i.icon),
      title: i.starterLabel?.trim() || i.title,
      reason: t(`rail.starters.insight.reason.${reasonKey}`),
      prompt: i.suggestedPrompt!,
    });
  }

  // 2. Team KPIs that are off track or stalling.
  const defById = new Map(inputs.kpiDefinitions.map((d) => [d.id, d]));
  const kpis = inputs.kpiRows
    .filter((r) => r.subjectKind === "team" && r.status in KPI_STATUS_RANK)
    .map((row) => ({ row, def: defById.get(row.kpiId) }))
    .filter((c): c is { row: KpiStatusRow; def: KpiDefinition } => !!c.def)
    .sort(
      (a, b) =>
        (KPI_STATUS_RANK[a.row.status] ?? 9) -
        (KPI_STATUS_RANK[b.row.status] ?? 9),
    );
  const seenKpi = new Set<string>();
  for (const { row, def } of kpis) {
    if (seenKpi.has(def.id)) continue;
    seenKpi.add(def.id);
    const status = row.status === "off_track" ? "offTrack" : "stalling";
    take({
      id: `kpi:${def.id}`,
      icon: TrendingDown,
      title: t(`rail.starters.kpi.${status}.title`, { kpi: def.name }),
      reason: t(`rail.starters.kpi.${status}.reason`),
      prompt: t(`rail.starters.kpi.${status}.prompt`, { kpi: def.name }),
    });
  }

  // 3. Experiments: ended-but-unjudged first, then running ones nearest their end.
  const today = now.toISOString().slice(0, 10);
  const experiments = inputs.objectives
    .filter(
      (
        o,
      ): o is PlaybookObjectiveRecord & {
        experiment: NonNullable<PlaybookObjectiveRecord["experiment"]>;
      } => o.experiment != null,
    )
    .map((o) => ({ o, phase: experimentPhase(o.experiment, now) }))
    .filter((x) => x.phase === "ended" || x.phase === "running")
    .sort((a, b) =>
      a.phase !== b.phase
        ? a.phase === "ended"
          ? -1
          : 1
        : a.o.experiment.endsAt.localeCompare(b.o.experiment.endsAt),
    );
  for (const { o, phase } of experiments) {
    const endsToday = o.experiment.endsAt === today;
    const reason =
      phase === "ended"
        ? t("rail.starters.experiment.ended.reason")
        : endsToday
          ? t("rail.starters.experiment.running.reasonToday")
          : t("rail.starters.experiment.running.reason", {
              date: formatIsoDate(o.experiment.endsAt, language),
            });
    take({
      id: `experiment:${o.id}`,
      icon: FlaskConical,
      title: t(`rail.starters.experiment.${phase}.title`, { title: o.title }),
      reason,
      prompt: t(`rail.starters.experiment.${phase}.prompt`, {
        title: o.title,
      }),
    });
  }

  // 4. Evergreen questions fill whatever is left.
  for (const [key, icon] of GENERIC) {
    take({
      id: `generic.${key}`,
      icon,
      title: t(`rail.starters.generic.${key}.title`),
      reason: t(`rail.starters.generic.${key}.reason`),
      prompt: t(`rail.starters.generic.${key}.prompt`),
    });
  }

  return out;
}

/**
 * Live starters for the docked rail. Reuses the dashboard's insight list query
 * (same key, so it is one fetch shared with Today) plus the KPI status and
 * playbook queries; each is disabled until the business is known.
 */
export function useRailStarters(
  businessId: string | undefined,
  enabled: boolean,
): RailStarter[] {
  const { t, i18n } = useTranslation("intelligence");
  const trpc = useTRPC();
  const id = enabled ? businessId : undefined;
  const insightsQuery = useQuery(
    trpc.intelligence.insights.list.queryOptions(
      { businessId: id ?? "" },
      { enabled: !!id, staleTime: DASHBOARD_TRPC_STALE_MS },
    ),
  );
  const statusQuery = useKpiStatus(id);
  const definitionsQuery = useKpiDefinitions(id);
  const objectivesQuery = usePlaybookObjectives(id);

  return useMemo(
    () =>
      buildRailStarters({
        insights: (insightsQuery.data as InsightRecord[] | undefined) ?? [],
        kpiRows: statusQuery.data?.rows ?? [],
        kpiDefinitions:
          (definitionsQuery.data as KpiDefinition[] | undefined) ?? [],
        objectives:
          (objectivesQuery.data as PlaybookObjectiveRecord[] | undefined) ?? [],
        now: new Date(),
        language: i18n.language,
        t: (key, opts) => String(t(key, opts as never)),
      }),
    [
      insightsQuery.data,
      statusQuery.data,
      definitionsQuery.data,
      objectivesQuery.data,
      i18n.language,
      t,
    ],
  );
}
